import { Surface } from '@chopchop-se/ui';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { useAuthUser, usePermissionCheck } from '../../auth/auth-store';
import { PageHeader } from '../../components/PageHeader';
import { buildNavSections } from '../../app/nav';

/**
 * The overview page. Intentionally thin for now: it renders the shortcuts the
 * signed-in user actually has access to, derived from the same nav registry the
 * sidebar uses, so a module that ships a page also shows up here for free.
 * Wave-1 agents add KPI tiles by exporting them from their own area.
 */
export function DashboardPage(): ReactNode {
  const { t } = useTranslation(['shell', 'common']);
  const user = useAuthUser();
  const can = usePermissionCheck();
  const sections = buildNavSections(can).filter((section) => section.id !== 'overview');

  return (
    <>
      <PageHeader
        title={t('shell:dashboard.welcome', { name: user?.firstName || user?.email || '' })}
        subtitle={t('shell:dashboard.subtitle')}
      />

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {sections.map((section) => (
          <Surface key={section.id} className="p-5">
            <h2 className="mb-3 font-mono text-[0.8125rem] font-medium uppercase tracking-[0.14em] text-ink-muted">
              {section.labelKey ? t(section.labelKey) : t('shell:dashboard.quickLinks')}
            </h2>
            <ul className="flex flex-col gap-1">
              {section.items.map((item) => (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    className="flex items-center gap-2.5 rounded-lg px-2 py-1.5 text-sm text-ink-light transition-colors hover:bg-brand-600/[0.06] hover:text-ink"
                  >
                    {item.icon ? <item.icon className="size-4 text-ink-muted" aria-hidden /> : null}
                    <span>{t(item.labelKey)}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </Surface>
        ))}
      </div>
    </>
  );
}
